import { makeAutoObservable, runInAction } from 'mobx';
import fetchService from '../servises/fetch.service';
import utilsService from '../servises/utils.service';
import { registerAccountStateReset } from '../servises/account-state.service';

class MetricsStore {
    constructor() {
        makeAutoObservable(this);
    }

    cpu = {
        ext: '%',
        key: 'cpu',
        data: []
    };
    ram = {
        ext: '',
        key: 'ram',
        data: []
    };
    net = {
        ext: '',
        key: 'net',
        key2: 'net2',
        label: 'TX',
        label2: 'RX',
        data: []
    };
    disk = {
        ext: '',
        key: 'disk',
        data: []
    };
    disk_io = {
        ext: '',
        key: 'disk_io',
        data: []
    };
    connections = {ext: '', key: 'connections', data: []};
    processes = {ext: '', key: 'processes', data: []};
    file_handles = {ext: '', key: 'file_handles', data: []};
    sessions = {ext: '', key: 'sessions', data: []};
    since = '10min'; // 5min, 30min, 1hour, 2hours, 6hours, 12hours, 24hours
    selectorLoading = false;
    requestSequence = 0;

    async getMetrics(serverId) {
        const requestSequence = ++this.requestSequence;
        runInAction(() => {
            this.selectorLoading = true;
        });
        try {
            const response = await fetchService.authenticatedGet(
                `/metrics/${ serverId }?from=${ this.since }`);
            const responseData = response.data.data_o;
            const data = responseData === null ? [] : responseData;
            runInAction(() => {
                if (requestSequence === this.requestSequence && Array.isArray(data)) {
                    this.normalizeMetrics(data);
                }
            });
        } finally {
            runInAction(() => {
                if (requestSequence === this.requestSequence) {
                    this.selectorLoading = false;
                }
            });
        }
    }

    async setSince(since, serverId) {
        runInAction(() => {
            this.since = since;
        });
        await this.getMetrics(serverId);
    }

    clearMetrics() {
        this.requestSequence += 1;
        this.selectorLoading = false;
        this.cpu = {
            ext: '%',
            key: 'cpu',
            data: []
        };
        this.ram = {
            ext: '',
            key: 'ram',
            data: []
        };
        this.net = {
            ext: '',
            key: 'net',
            key2: 'net2',
            label: 'TX',
            label2: 'RX',
            data: []
        };
        this.disk = {
            ext: '',
            key: 'disk',
            data: []
        };
        this.disk_io = {
            ext: '',
            key: 'disk_io',
            data: []
        };
        this.connections = {ext: '', key: 'connections', data: []};
        this.processes = {ext: '', key: 'processes', data: []};
        this.file_handles = {ext: '', key: 'file_handles', data: []};
        this.sessions = {ext: '', key: 'sessions', data: []};
    }

    normalizeMetrics(data) {
        const len = data.length;

        const cpu = {
            ext: '%',
            key: 'cpu',
            data: []
        };

        const ram = {
            ext: '',
            key: 'ram',
            data: []
        };
        const net = {
            ext: '',
            key: 'net',
            key2: 'net2',
            label: 'TX',
            label2: 'RX',
            data: []
        };
        const disk = {
            ext: '',
            key: 'disk',
            data: []
        };
        const disk_io = {
            ext: '',
            key: 'disk_io',
            data: []
        };

        const connections = {
            ext: '',
            key: 'connections',
            data: []
        };
        const processes = {
            ext: '',
            key: 'processes',
            data: []
        };
        const file_handles = {
            ext: '',
            key: 'file_handles',
            data: []
        };

        const sessions = {
            ext: '',
            key: 'sessions',
            data: []
        };

        const seriesMaximums = data.reduce((maximums, metric) => {
            const diskIoBytes = Array.isArray(metric.disk_io) ? metric.disk_io.reduce(
                (total, io) => total + (io.read_bytes_per_sec || 0) + (io.write_bytes_per_sec || 0),
                0
            ) : 0;
            return {
                ram: Math.max(maximums.ram, metric.ram_usage || 0, metric.ram_total || 0),
                net: Math.max(maximums.net, metric.tx_gap || 0, metric.rx_gap || 0),
                disk: Math.max(maximums.disk, metric.disk_usage || 0, metric.disk_total || 0),
                diskIo: Math.max(maximums.diskIo, diskIoBytes)
            };
        }, {ram: 0, net: 0, disk: 0, diskIo: 0});
        const ramUnit = utilsService.formatBytes(seriesMaximums.ram).unit;
        const netUnit = utilsService.formatBytes(seriesMaximums.net).unit;
        const diskUnit = utilsService.formatBytes(seriesMaximums.disk).unit;
        const diskIoUnit = utilsService.formatBytes(seriesMaximums.diskIo).unit;

        ram.ext = ramUnit;
        net.ext = netUnit;
        disk.ext = diskUnit;
        disk_io.ext = `${ diskIoUnit }/s`;

        for (let i = 0; i < len; i++) {
            const metric = data[i];

            const date = utilsService.timeMe(metric.createdAt);

            const connectionData = metric.connections;
            const processesData = metric.processes;
            const file_handlesData = metric.file_handles;
            const sessionsData = metric.sessions;

            cpu.data.push({
                name: date,
                cpu: metric.load_cpu,
                total: 100
            });

            ram.data.push({
                name: date,
                ram: utilsService.formatByTesByUnit(metric.ram_usage || 0, ramUnit),
                total: utilsService.formatByTesByUnit(metric.ram_total || 0, ramUnit)
            });

            net.data.push({
                name: date,
                net: utilsService.formatByTesByUnit(
                    metric.tx_gap || 0,
                    netUnit
                ),
                net2: utilsService.formatByTesByUnit(
                    metric.rx_gap || 0,
                    netUnit
                )
            });

            disk.data.push({
                name: date,
                disk: utilsService.formatByTesByUnit(metric.disk_usage || 0, diskUnit),
                total: utilsService.formatByTesByUnit(metric.disk_total || 0, diskUnit)
            });

            let totalDiskIoBytes = 0;
            if (metric.disk_io && Array.isArray(metric.disk_io)) {
                metric.disk_io.forEach(io => {
                    totalDiskIoBytes += (io.read_bytes_per_sec || 0) + (io.write_bytes_per_sec || 0);
                });
            }
            disk_io.data.push({
                name: date,
                disk_io: utilsService.formatByTesByUnit(totalDiskIoBytes, diskIoUnit)
            });

            connections.data.push({
                name: date,
                connections: connectionData
            });

            processes.data.push({
                name: date,
                processes: processesData
            });

            sessions.data.push({
                name: date,
                sessions: sessionsData
            });

            file_handles.data.push({
                name: date,
                file_handles: file_handlesData,
                // total: metric.file_handles_limit.high
            });
        }

        this.cpu = cpu;
        this.ram = ram;
        this.net = net;
        this.disk = disk;
        this.disk_io = disk_io;
        this.connections = connections;
        this.processes = processes;
        this.file_handles = file_handles;
        this.sessions = sessions;
    }
}

const metricsStore = new MetricsStore();
registerAccountStateReset(() => metricsStore.clearMetrics());

export default metricsStore;
